import { ApiError, toApiError } from "./errors.js";
import { ClockOffset } from "./clock.js";
import { createLocalStorageSessionStorage, type SessionStorage } from "./storage.js";
import type {
  BrowseGroup,
  BrowseTrack,
  MyRequestsResult,
  NearbyVenue,
  Page,
  RequestResult,
  SessionGrant,
  StateSnapshot,
  Surface,
  VenueContext,
  VenueSearchResult,
} from "./types.js";

export interface TouchscreenApiClientOptions {
  /** e.g. `https://api.example.com/api/touchscreen`. No trailing slash. */
  baseUrl: string;
  surface: Surface;
  /** Shared dev key — permits an arbitrary venue reference from anywhere, including production. */
  devKey?: string;
  storage?: SessionStorage;
  fetch?: typeof fetch;
}

/** Returned by `getState`; distinguishes "nothing changed" from "no data yet". */
export type StateResult = { changed: true; snapshot: StateSnapshot } | { changed: false };

/**
 * Owns the whole session grant lifecycle described in touchscreen-api.md §Authentication:
 * obtain a venue reference (caller's job), `POST /session`, bearer the token, and transparently
 * re-session on `401 token_expired`. Callers should never see `token_expired` — it is retried
 * once, invisibly. `401 token_invalid` is different: it clears the stored grant and rethrows, so
 * the caller must restart from step 1 (re-resolve the venue reference).
 */
export class TouchscreenApiClient {
  private readonly baseUrl: string;
  private readonly surface: Surface;
  private readonly devKey: string | undefined;
  private readonly storage: SessionStorage;
  private readonly fetchImpl: typeof fetch;
  private readonly clock = new ClockOffset();

  private sessionToken: string | null = null;
  private requesterId: string | null = null;
  private venueRef: string | null = null;
  private venue: VenueContext | null = null;
  /** Serialises concurrent re-session attempts so a burst of 401s doesn't fire N sessions. */
  private pendingSession: Promise<SessionGrant> | null = null;

  constructor(options: TouchscreenApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.surface = options.surface;
    this.devKey = options.devKey;
    this.storage = options.storage ?? createLocalStorageSessionStorage();
    // Never store a bare reference to the global `fetch` — calling it later as `this.fetchImpl(...)`
    // invokes it with `this` bound to the client instance, and `fetch` requires `this` to be a
    // real Window/WorkerGlobalScope, so that throws "Illegal invocation" before any request is
    // ever dispatched (silently, with nothing to show in the Network tab). Wrapping it so it's
    // always called as a bare global call sidesteps the receiver check entirely.
    this.fetchImpl = options.fetch ?? ((...args) => fetch(...args));

    const persisted = this.storage.read();
    if (persisted && persisted.surface === this.surface) {
      this.sessionToken = persisted.sessionToken;
      this.requesterId = persisted.requesterId;
      this.venueRef = persisted.venueRef;
    }
  }

  getClockOffset(): ClockOffset {
    return this.clock;
  }

  getVenueContext(): VenueContext | null {
    return this.venue;
  }

  getRequesterId(): string | null {
    return this.requesterId;
  }

  /** True once a grant exists in memory or was restored from storage for this surface. */
  hasSession(): boolean {
    return this.sessionToken !== null;
  }

  /**
   * `POST /session`. Pass `requesterId` explicitly to resume a returning punter (e.g. after
   * restoring from storage); omit to be issued a new one.
   */
  async createSession(venueRef: string, opts?: { requesterId?: string }): Promise<SessionGrant> {
    const body: Record<string, unknown> = {
      venue: venueRef,
      surface: this.surface,
    };
    const requesterId = opts?.requesterId ?? this.requesterId;
    if (requesterId) body.requester_id = requesterId;
    if (this.devKey) body.dev = this.devKey;

    const grant = await this.rawRequest<SessionGrant>("POST", "/session", { body, authenticated: false });

    this.sessionToken = grant.session_token;
    this.requesterId = grant.requester_id;
    this.venueRef = venueRef;
    this.venue = grant.venue;

    this.storage.write({
      sessionToken: grant.session_token,
      requesterId: grant.requester_id,
      venueRef,
      surface: this.surface,
      expiresAt: grant.expires_at,
    });

    return grant;
  }

  /**
   * Establishes a session if none is held yet, using the persisted requester id when present.
   * Returns the venue context either way — callers that don't need `session_token` directly
   * (everything goes through `authedRequest`) should prefer this over `createSession`.
   *
   * Coalesces concurrent callers onto the same in-flight grant via `pendingSession` — the same
   * guard `reSession` uses — so a burst of near-simultaneous calls (e.g. React StrictMode's
   * double-mount, or two components booting off the same client) fires exactly one `POST /session`
   * instead of one per caller.
   */
  async ensureSession(venueRef: string): Promise<VenueContext> {
    if (this.sessionToken && this.venueRef === venueRef && this.venue) {
      return this.venue;
    }
    if (!this.pendingSession) {
      this.pendingSession = this.createSession(
        venueRef,
        this.requesterId ? { requesterId: this.requesterId } : undefined,
      ).finally(() => {
        this.pendingSession = null;
      });
    }
    const grant = await this.pendingSession;
    return grant.venue;
  }

  /** Phone only. Does not require a session — the venue isn't known yet. */
  async venuesNearby(lat: number, lng: number): Promise<NearbyVenue[]> {
    return this.rawRequest<NearbyVenue[]>("GET", `/venues/nearby?${new URLSearchParams({
      lat: String(lat),
      lng: String(lng),
    })}`, { authenticated: false });
  }

  /**
   * Phone only. Does not require a session. Free-text venue name/operator/town/county/postcode
   * search — a directory leak by nature, so it's deliberately narrow (`422 query_too_short` below
   * the config-driven minimum, small result cap, heavier rate limiting than `venuesNearby`). Use
   * this for a typed-in venue name; use `createSession`/`ensureSession` directly when the caller
   * already has a `JUKE\d+`-shaped ref and doesn't need to search at all.
   */
  async venuesSearch(query: string): Promise<VenueSearchResult[]> {
    return this.rawRequest<VenueSearchResult[]>(
      "GET",
      `/venues/search?${new URLSearchParams({ q: query })}`,
      { authenticated: false },
    );
  }

  /**
   * The composite snapshot clients poll. Pass the last-seen `version`; a `304` comes back as
   * `{ changed: false }` with no clock update — keep using the last offset measured.
   */
  async getState(sinceVersion?: number): Promise<StateResult> {
    const qs = sinceVersion !== undefined ? `?since_version=${sinceVersion}` : "";
    const result = await this.authedRequest<StateSnapshot | null>("GET", `/state${qs}`, {
      treat304As: null,
    });
    if (result === null) return { changed: false };
    this.clock.update(result.server_now_ms);
    return { changed: true, snapshot: result };
  }

  /** Groups only — no tracks. Deterministic, cacheable order; shuffle presentation client-side. */
  async browse(): Promise<BrowseGroup[]> {
    return this.authedRequest<BrowseGroup[]>("GET", "/browse");
  }

  async browseGroupTracks(groupId: number, params?: { page?: number; perPage?: number }): Promise<Page<BrowseTrack>> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.perPage) qs.set("per_page", String(params.perPage));
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.authedPagedRequest<BrowseTrack>("GET", `/browse/${groupId}/tracks${suffix}`, params);
  }

  /** `403 search_disabled` when `venue.search.enabled` is false — check that before calling. */
  async search(query: string, params?: { page?: number; perPage?: number }): Promise<Page<BrowseTrack>> {
    const qs = new URLSearchParams({ q: query });
    if (params?.page) qs.set("page", String(params.page));
    if (params?.perPage) qs.set("per_page", String(params.perPage));
    return this.authedPagedRequest<BrowseTrack>("GET", `/search?${qs}`, params);
  }

  /**
   * `duplicate: true` in the result is success — the track is queued, just not by this punter.
   * Always pass a stable `idempotencyKey`; a replay returns the original request, not an error.
   */
  async requestTrack(
    smId: string,
    idempotencyKey: string,
    position?: { lat: number; lng: number },
  ): Promise<RequestResult> {
    const body: Record<string, unknown> = { sm_id: smId, idempotency_key: idempotencyKey };
    if (position) {
      body.lat = position.lat;
      body.lng = position.lng;
    }
    return this.authedRequest<RequestResult>("POST", "/requests", { body });
  }

  async requestsMine(): Promise<MyRequestsResult> {
    return this.authedRequest<MyRequestsResult>("GET", "/requests/mine");
  }

  /** Clears the stored grant. Call after `token_invalid`, or when a punter switches venue. */
  clearSession(): void {
    this.sessionToken = null;
    this.requesterId = null;
    this.venueRef = null;
    this.venue = null;
    this.storage.clear();
  }

  /**
   * Bearer the current token; on `401 token_expired` re-session transparently and retry exactly
   * once. On `401 token_invalid`, clear the grant and rethrow — the caller must restart from a
   * fresh venue reference, not retry.
   */
  private async authedRequest<T>(
    method: "GET" | "POST",
    path: string,
    opts?: { body?: unknown; treat304As?: T; unwrapEnvelope?: boolean },
  ): Promise<T> {
    if (!this.venueRef) {
      throw new ApiError({ code: "token_invalid", status: 401, message: "No active session — call createSession first." });
    }
    const rawOpts = { body: opts?.body, authenticated: true, treat304As: opts?.treat304As, unwrapEnvelope: opts?.unwrapEnvelope };
    try {
      return await this.rawRequest<T>(method, path, rawOpts);
    } catch (err) {
      if (err instanceof ApiError && err.code === "token_expired") {
        await this.reSession();
        return this.rawRequest<T>(method, path, rawOpts);
      }
      if (err instanceof ApiError && err.code === "token_invalid") {
        this.clearSession();
      }
      throw err;
    }
  }

  /**
   * `/browse/{group}/tracks` and `/search` return `{ success, data: T[], meta: {page, per_page,
   * total} }` — the pagination info lives beside `data`, not inside it, so it needs its own
   * unwrap into `Page<T>` rather than the plain `data`-only unwrap `authedRequest` does.
   */
  private async authedPagedRequest<T>(
    method: "GET" | "POST",
    path: string,
    params?: { page?: number; perPage?: number },
  ): Promise<Page<T>> {
    const { data, meta } = await this.authedRequest<{
      data: T[];
      meta?: { page?: number; per_page?: number; total?: number | null };
    }>(method, path, { unwrapEnvelope: false });
    return {
      items: data,
      page: meta?.page ?? params?.page ?? 1,
      per_page: meta?.per_page ?? params?.perPage ?? data.length,
      total: meta?.total ?? null,
    };
  }

  private async reSession(): Promise<void> {
    if (!this.venueRef) throw new ApiError({ code: "token_invalid", status: 401 });
    if (!this.pendingSession) {
      this.pendingSession = this.createSession(this.venueRef, this.requesterId ? { requesterId: this.requesterId } : undefined).finally(() => {
        this.pendingSession = null;
      });
    }
    await this.pendingSession;
  }

  private async rawRequest<T>(
    method: "GET" | "POST",
    path: string,
    opts: { body?: unknown; authenticated: boolean; treat304As?: T; unwrapEnvelope?: boolean },
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (opts.authenticated) {
      if (!this.sessionToken) {
        throw new ApiError({ code: "token_invalid", status: 401, message: "No session token held." });
      }
      headers.Authorization = `Bearer ${this.sessionToken}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      });
    } catch (networkErr) {
      throw new ApiError({
        code: "network_error",
        status: 0,
        message: networkErr instanceof Error ? networkErr.message : "Network request failed",
      });
    }

    if (response.status === 304) {
      if ("treat304As" in opts) return opts.treat304As as T;
      throw new ApiError({ code: "unknown", status: 304, message: "Unexpected 304 for a non-cacheable call." });
    }

    if (!response.ok) {
      const errorBody = await safeJson(response);
      throw toApiError(response.status, errorBody);
    }

    const parsed = await safeJson(response);
    if (isEnvelope(parsed)) return (opts.unwrapEnvelope === false ? parsed : parsed.data) as T;
    return parsed as T;
  }
}

interface Envelope {
  success: true;
  data: unknown;
  meta?: unknown;
}

function isEnvelope(value: unknown): value is Envelope {
  return typeof value === "object" && value !== null && "success" in value && "data" in value;
}

async function safeJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
